/**
 * Documento do titular (CPF/CNPJ).
 *
 * O documento NUNCA vem do input do usuário: ele é extraído da resposta da API
 * para a linha validada. Isso é uma regra de segurança herdada do legado
 * (`PlanoController::extractDocumentFromSession`) e não pode ser afrouxada.
 */

import { onlyDigits } from './phone';

/** Chaves já observadas na resposta da API, em ordem de prioridade. */
const DOCUMENT_KEYS = [
  // A API do Recarga Fácil devolve o documento JÁ MASCARADO — o valor completo
  // nunca chega ao navegador. Vem primeiro por ser o formato atual.
  'documento_mascarado',
  'documento_titular',
  'cpf_titular',
  'cnpj_titular',
  'documento',
  'cpf',
  'cnpj',
  'doc',
  'cliente_documento',
  'cliente_cpf',
  'cliente_cnpj',
];

type UnknownRecord = Record<string, unknown>;

function pickDocument(source: UnknownRecord | null | undefined): string | null {
  if (!source) return null;
  for (const key of DOCUMENT_KEYS) {
    const value = source[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
    if (typeof value === 'number') return String(value);
  }
  return null;
}

/**
 * Extrai o documento do payload de detalhes da linha. Procura primeiro dentro
 * de `detalhes` (formato atual da API) e depois na raiz (formatos antigos).
 */
export function extractDocument(lineData: UnknownRecord | null | undefined): string | null {
  if (!lineData) return null;
  const detalhes = lineData.detalhes as UnknownRecord | undefined;
  return pickDocument(detalhes) ?? pickDocument(lineData);
}

/**
 * `12345678901` → `123.456.789-01`; `12345678000199` → `12.345.678/0001-99`.
 *
 * Um documento que já vem mascarado da API (`***.***.111-**`) é devolvido como
 * está: mascarar de novo destruiria o pouco que resta para o cliente conferir.
 */
export function formatDocument(value: string | null | undefined): string {
  const raw = String(value ?? '');
  if (raw.includes('*')) return raw;
  const digits = onlyDigits(raw);
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return raw;
}
